// https://vuex.vuejs.org/ja/guide/actions.html

import mutationTypes from '@/store/mutationTypes';
import { API, graphqlOperation, Logger } from 'aws-amplify';
import {
  byCategory,
  byContributedYear,
  getNotification,
  getWelfare,
} from '@/graphql/queries';
import { ModelSortDirection } from '@/graphql/types';
import { welfareByContributedYear } from '../graphql/queries';
//import {KeyStatus} from '../graphql/types';

const logger = new Logger('actions');

// minimumおしらせ取得年度
const MIN_NOTIFICATION_YEAR = 2022;

// minimum福利厚生取得年度
const MIN_WELFARE_YEAR = 2022;

/**
 * ファイルテーブルのカテゴリ
 * @type {{MANUAL: string}}
 * @type {{UIUX: string}}
 */
export const FileCategory = {
  MANUAL: 'manual',
  UIUX: 'ui_ux',
};

const Actions = {
  /**
   * 画面タイトル設定
   * @param context
   * @param payload
   */
  setPageTitle: (context, payload) => {
    context.commit(mutationTypes.SET_PAGE_TITLE, payload);
  },

  /**
   * 最初にアクセスされたルートを設定
   * @param context
   * @param payload
   */
  setInitialRoute: (context, payload) => {
    context.commit(mutationTypes.SET_INITIAL_ROUTE, payload);
  },

  /**
   * 最初にアクセスされたルートを設定を復元する
   * @param context
   */
  restoreInitialRoute: (context) => {
    context.commit(mutationTypes.RESTORE_INITIAL_ROUTE);
  },

  /**
   * ログイン
   * @param context
   * @param payload
   */
  login: (context, payload) => {
    logger.info('login payload', payload);
    context.commit(mutationTypes.SET_AUTH, { auth: payload.auth });
  },

  /**
   * ログアウト
   * @param context
   */
  logout: (context) => {
    logger.info('logout');
    context.commit(mutationTypes.SET_AUTH, { auth: null });
  },

  /**
   * おしらせ一覧を取得する
   */
  getNotifications: async (context, payload) => {
    logger.info('getNotifications', payload);

    try {
      const results = await notificationsByContributedYearApi(
        payload.count, payload.year);
      logger.info('getNotifications.results', results);
      context.commit(mutationTypes.SET_NOTIFICATIONS, results);
      return true;
    } catch (e) {
      logger.error('getNotifications', e);
    }

    return false;
  },

  /**
   * idを指定してお知らせ取得
   * @param context
   * @param payload
   * @returns {Promise<boolean>}
   */
  getNotificationBy: async (context, payload) => {
    logger.info('getNotificationBy', payload);
    const id = payload.id;

    // 取得済みであればそれを返す
    // NOTE: 更新頻度は高くないはずなので、キャッシュで問題ない想定
    const result = context.getters.notifications.first(
      x => x.id === id);
    if (result) {
      return result;
    }

    // なければ取得する
    try {
      const result = await API.graphql(graphqlOperation(getNotification, {
        id: id,
      }));
      logger.info('getNotification.result', result);
      context.commit(
        mutationTypes.SET_NOTIFICATION, result.data.getNotification);

      return true;
    } catch (e) {
      logger.error('getNotificationBy', e);
    }

    return false;
  },

  /**
   * カテゴリを指定してファイル情報一覧を取得する
   * @param context
   * @param payload
   * @returns {Promise<boolean>}
   */
  getFilesByCategory: async (context, payload) => {
    logger.info('getFilesByCategory', payload);
    const category = payload;

    try {
      const result = await API.graphql(graphqlOperation(byCategory, {
        category: category,
      }));
      logger.info('getFilesByCategory.result', result);

      context.commit(mutationTypes.SET_FILES, {
        category: category,
        results: result.data.byCategory.items,
      });
      return true;
    } catch (e) {
      logger.error('getFilesByCategory', e);
    }

    return false;
  },
  /**
   * 制度・福利厚生一覧を取得する
   */
  getWelfares: async (context, payload) => {
    logger.info('getWelfares', payload);

    try {
      const results = await welfaresByContributedYearApi(
          payload.count, payload.year);
      logger.info('getWelfares.results', results);
      context.commit(mutationTypes.SET_WELFARES, results);
      return true;
    } catch (e) {
      logger.error('getWelfares', e);
    }

    return false;
  },

  /**
   * idを指定して制度・福利厚生取得
   * @param context
   * @param payload
   * @returns {Promise<boolean>}
   */
  getWelfareBy: async (context, payload) => {
    logger.info('getWelfareBy', payload);
    const id = payload.id;

    // 取得済みであればそれを返す
    // NOTE: 更新頻度は高くないはずなので、キャッシュで問題ない想定
    const result = context.getters.welfares.first(
        x => x.id === id);
    if (result) {
      return result;
    }

    // なければ取得する
    try {
      const result = await API.graphql(graphqlOperation(getWelfare, {
        id: id,
      }));
      logger.info('getWelfare.result', result);
      context.commit(
          mutationTypes.SET_WELFARE, result.data.getWelfare);

      return true;
    } catch (e) {
      logger.error('getWelfareBy', e);
    }

    return false;
  },
};

/**
 * 年を指定してcount分までおしらせ一覧を取得する
 * 指定年度で足りなければ遡る
 * @param count
 * @param year
 * @returns {Promise<boolean|*[]>}
 */
async function notificationsByContributedYearApi(count, year) {
  logger.info('byContributedYearApi');
  if (year < MIN_NOTIFICATION_YEAR) {
    throw new RangeError('Year out of bounds');
  }

  try {
    // おしらせがcount分取得できるまで、yearからMIN_NOTIFICATION_YEARまで遡る
    let results = [];

    for (let i = year; i >= MIN_NOTIFICATION_YEAR; i--) {
      const result = await API.graphql(graphqlOperation(byContributedYear, {
        contributedYear: i,
        sortDirection: ModelSortDirection.DESC,
        limit: count,
      }));
      logger.info('byContributedYearApi.result', result);

      // マージする
      results = results.concat(result.data.byContributedYear.items).
          slice(0, count);

      // 必要なお知らせ分取得済みの場合
      if (results.length === count) {
        break;
      }
    }

    return results;
  } catch (e) {
    logger.error('byContributedYearApi', e);
  }

  return false;
}

/**
 * 年を指定してcount分まで制度・福利厚生一覧を取得する
 * 指定年度で足りなければ遡る
 * @param count
 * @param year
 * @returns {Promise<boolean|*[]>}
 */
async function welfaresByContributedYearApi(count, year) {
  logger.info('byContributedYearApi');
  if (year < MIN_WELFARE_YEAR) {
    throw new RangeError('Year out of bounds');
  }

  try {
    // 制度・福利厚生がcount分取得できるまで、yearからMIN_WELFARE_YEARまで遡る
    let results = [];

    for (let i = year; i >= MIN_WELFARE_YEAR; i--) {
      const result = await API.graphql(
          graphqlOperation(welfareByContributedYear, {
            contributedYear: i,
            sortDirection: ModelSortDirection.DESC,
            limit: count,
          }));
      logger.info('welfareByContributedYear.result', result);

      // マージする
      results = results.concat(result.data.welfareByContributedYear.items).
          slice(0, count);

      // 必要な制度・福利厚生分取得済みの場合
      if (results.length === count) {
        break;
      }
    }

    return results;
  } catch (e) {
    logger.error('welfareByContributedYearApi', e);
  }

  return false;
}

export default Actions;
